import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATES, TIERS } from "@/components/leadUtils";
import { MapPin, Users, Lock, CheckCircle2, Flame, Loader2, Search } from "lucide-react";

const ZipCard = ({ z, busy, onSubscribe, onCancel }) => {
  const tier = TIERS[z.tier];
  return (
    <Card data-testid={`zip-card-${z.zip_code}`} className={`bg-slate-900/80 border-slate-800 rounded-xl p-5 flex flex-col gap-4 transition-colors ${z.owned_by_me ? "border-emerald-500/60" : "hover:border-emerald-500/40"} ${z.taken && !z.owned_by_me ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading text-2xl font-extrabold tracking-tight">{z.zip_code}</div>
          <div className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" />{z.city}, {z.state}</div>
        </div>
        <Badge className={`font-mono text-[10px] uppercase tracking-wider border whitespace-nowrap shrink-0 ${tier.cls}`}>{tier.label}</Badge>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-slate-500">
        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{z.population.toLocaleString()} ppl</span>
        <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-rose-400" />{z.lead_count} lead{z.lead_count === 1 ? "" : "s"} · {z.hot_count} hot</span>
      </div>
      <div className="flex items-center justify-between mt-auto">
        <div className="font-heading text-xl font-bold text-emerald-400" data-testid={`zip-price-${z.zip_code}`}>${z.price}<span className="text-xs text-slate-500 font-normal">/mo</span></div>
        {z.owned_by_me ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(z)} data-testid={`zip-cancel-btn-${z.zip_code}`} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-full">
            <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-400" /> Yours · Release
          </Button>
        ) : z.taken ? (
          <Badge className="bg-slate-800 text-slate-400 border border-slate-700 gap-1" data-testid={`zip-taken-${z.zip_code}`}><Lock className="h-3 w-3" /> Taken</Badge>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => onSubscribe(z)} data-testid={`zip-subscribe-btn-${z.zip_code}`} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-5">
            {busy ? "…" : "Subscribe"}
          </Button>
        )}
      </div>
    </Card>
  );
};

export const TerritoryMarketplace = ({ onChange }) => {
  const [zips, setZips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filters, setFilters] = useState({ state: "all", tier: "all", search: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/zips", { params });
      setZips(data.zips);
    } catch (e) {
      toast.error("Failed to load ZIP territories");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const act = async (z, fn, msg) => {
    setBusy(z.zip_code);
    try {
      await fn();
      toast.success(msg);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(null);
      load();
    }
  };
  const subscribe = (z) => act(z, () => api.post("/subscriptions", { zip_code: z.zip_code }), `ZIP ${z.zip_code} is now exclusively yours — $${z.price}/mo.`);
  const cancel = (z) => window.confirm(`Release ZIP ${z.zip_code}? You'll stop receiving its leads and another agent can take it.`) && act(z, () => api.delete(`/subscriptions/${z.zip_code}`), `ZIP ${z.zip_code} released.`);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="zip-search-input" placeholder="ZIP or city" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-9 w-[200px] bg-slate-900 border-slate-800" />
        </div>
        <Select value={filters.state} onValueChange={(v) => setFilters((f) => ({ ...f, state: v }))}>
          <SelectTrigger data-testid="zip-state-filter" className="w-[170px] bg-slate-900 border-slate-800"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.tier} onValueChange={(v) => setFilters((f) => ({ ...f, tier: v }))}>
          <SelectTrigger data-testid="zip-tier-filter" className="w-[200px] bg-slate-900 border-slate-800"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            {Object.entries(TIERS).map(([k, t]) => <SelectItem key={k} value={k}>{t.label} · ${t.price}/mo</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
      ) : zips.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 rounded-xl p-16 text-center text-slate-400">No ZIP territories match your filters.</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="zip-marketplace-grid">
          {zips.map((z) => <ZipCard key={z.zip_code} z={z} busy={busy === z.zip_code} onSubscribe={subscribe} onCancel={cancel} />)}
        </div>
      )}
    </div>
  );
};
